import {
    NextRouter
} from 'next/router';
import {
    get
} from '../networking';
import {
    SubscriptionState
} from './stripe';
import * as SHA1 from 'crypto-js/sha1';
import {
    FlowType
} from '../components/start/flow-type.enum';
import {
    GetServerSidePropsContext, GetServerSidePropsResult
} from 'next';
import { ampSetUserId } from '../lib/amplitude';
import {
    getCurrentWeekId, getWeekIdForDay
} from '../utils/dateHelpers';
import {
    AppUserAgent
} from '../components/core/use-app-user-agent';
import {
    interpretUserPaymentEntityState
} from './payments-gateway';

const ENVIRONMENT = process.env.NEXT_PUBLIC_NENV;

/* SUNNYSIDE DIAGNOSTIC FORM IDS */
/* SS digital signup diagnostic form id */
export const activeFormId = 'ss_20250815';
/* SS diagnostic form ids (upsell) */
export const naltrexoneOnlyUpsellDiagnosticFormId = 'pmat_20250423'; // The OG naltrexone only funnel for existing users
/* SS diagnostic form ids (med first) - legacy */
export const naltrexoneOnlyDiagnosticFormId = 'mf_20250815'; // The OG naltrexone only funnel for new users (legacy)
export const naltrexoneOnlyV2DiagnosticFormId = 'mf_20260601'; // Same as above, but updated styles
/* SS diagnostic form ids (med first) - new */
export const naltrexoneFirstV2RecommendationDiagnosticFormId = 'mf_20260601-2'; // New recommendation funnel which always recommends naltrexone, but shows eligible plans below
export const glp1DiagnosticFormId = 'mf_20260521-1'; // New recommendation funnel which recommends the best eligible plan - with a focus on GLP1
export const genericSunnysideMedDiagnosticFormId = 'mf_20260521-2'; // New recommendation funnel which recommends the best eligible plan
export const naltrexoneV2RecommendationDiagnosticFormId = 'mf_20261008'; // New recommendation funnel which recommends the best eligible plan - with a focus on naltrexone
export const genericAdSafeSunnysideMedDiagnosticFormId = 'mf_20261014'; // New recommendation funnel which recommends the best eligible plan - no mention of semaglutide to be ad-safe on old pixel

/* OPENLOOP INTAKE FORM IDS */
/* OL naltrexone Intake Form Ids - legacy */
export const naltrexoneOnlyIntakeProdFormId = '2533881';
export const naltrexoneOnlyIntakeStagingFormId = '2164397';
export const naltrexoneOnlyUpsellIntakeFormId = ENVIRONMENT === 'production' ? `mat_${naltrexoneOnlyIntakeProdFormId}` : `mat_${naltrexoneOnlyIntakeStagingFormId}`;
export const naltrexoneOnlyIntakeOpenloopFormId = ENVIRONMENT === 'production'
    ? naltrexoneOnlyIntakeProdFormId
    : naltrexoneOnlyIntakeStagingFormId;
/* OL naltrexone Refill Form Ids - legacy */
export const naltrexoneOnlyRefillProdFormId = '2902024';
export const naltrexoneOnlyRefillStagingFormId = '2248244';
export const naltrexoneOnlyRefillFormId = ENVIRONMENT === 'production' ? `mat_v2_${naltrexoneOnlyRefillProdFormId}` : `mat_v2_${naltrexoneOnlyRefillStagingFormId}`;
export const naltrexoneOnlyRefillOpenloopFormId = ENVIRONMENT === 'production'
    ? naltrexoneOnlyRefillProdFormId
    : naltrexoneOnlyRefillStagingFormId;

/* OL Combined Intake Form Ids - new */
export const combinedIntakeProdFormId = '3072437';
export const combinedIntakeStagingFormId = '2303003';
export const combinedIntakeOpenloopFormId = ENVIRONMENT === 'production'
    ? combinedIntakeProdFormId
    : combinedIntakeStagingFormId;
/* OL Combined Intake (Naltrexone) Refill Form Ids - new */
export const naltrexoneOnlyV2RefillProdFormId = '3077230';
export const naltrexoneOnlyV2RefillStagingFormId = '2306044';
export const naltrexoneOnlyV2RefillFormId = ENVIRONMENT === 'production' ? `mat_v2_${naltrexoneOnlyV2RefillProdFormId}` : `mat_v2_${naltrexoneOnlyV2RefillStagingFormId}`;
export const naltrexoneOnlyV2RefillOpenloopFormId = ENVIRONMENT === 'production'
    ? naltrexoneOnlyV2RefillProdFormId
    : naltrexoneOnlyV2RefillStagingFormId;
/* OL Combined Intake (GLP1) Refill Form Ids - new */
export const glp1RefillProdFormId = '3077231';
export const glp1RefillStagingFormId = '2306045';
export const glp1RefillFormId = ENVIRONMENT === 'production' ? `mat_v2_${glp1RefillProdFormId}` : `mat_v2_${glp1RefillStagingFormId}`;
export const glp1RefillOpenloopFormId = ENVIRONMENT === 'production'
    ? glp1RefillProdFormId
    : glp1RefillStagingFormId;

/* OL AUD Encounter Note Form Ids*/
/** The encounter note that replaces the legacy one; development and staging share a form. */
export const audEncounterProdFormId = '3076000';
export const audEncounterStagingFormId = '2305318';
export const audEncounterOpenloopFormId = ENVIRONMENT === 'production'
    ? audEncounterProdFormId
    : audEncounterStagingFormId;

export async function refreshUserAndStoreIn(session) {
    const userData = await get('users/current-user');
    session.setUser(userData);
    localStorage.setItem('userId', userData.id);
    if (userData.id) {
        ampSetUserId(userData.id);
    }
    window && window['ire'] && window['ire']('identify', {
        customerId: userData.id,
        customerEmail: SHA1(userData.email)
    });
    return userData;
}

export async function redirectUserAfterLogin(
    user,
    router: NextRouter,
    redirectUrl?: string | string[],
    formId: string = activeFormId,
    setHasAccess?: (hasAccess: boolean) => void,
    cookies?: { [key: string]: any },
    appUserAgent?: AppUserAgent
) {
    const defaultRedirectFlow = async () => {
        const isUserFirstWeek = getWeekIdForDay(user.createdAt) === getCurrentWeekId(user.timeZone);
        const isMedFirstOrWinback = [FlowType.MED_FIRST, FlowType.MED_FIRST_WINBACK].includes(user.flowType);
        const isMedUpsellWinback = user.flowType === FlowType.MED_UPSELL_WINBACK;
        if (user.id) {
            ampSetUserId(user.id);
        }
        if (redirectUrl && !formId.startsWith('ss_') && !isMedFirstOrWinback) {
            if (cookies?.ss_app === 'true' || appUserAgent?.isApp) {
                router.push('/app-bridge');
                return;
            }
            router.push(redirectUrl as string);
            return;
        } else if (user.diagnosticInFlight) {
            const serverFormId = user.formId;
            if (isMedFirstOrWinback) {
                router.push(`/mf/diagnostic/${user.formId}`); // TODO YEHIA: Figure something out
                return;
            } else if (isMedUpsellWinback) {
                router.push('/medication/diagnostic'); // TODO YEHIA: Figure something out
                return;
            }
            /**
             * If user has phone but diagnostic is inFlight then they have reached phone verification
             * but dropped off which means they have 'completed' the diagnostic. This way we don't force
             * them back through diagnostic to get where they dropped off.
             */
            if (user.phone && formId.startsWith('ss_')) {
                if (serverFormId === 'ss_20240708' || formId === 'ss_20240708') router.push('/diagnostic/ss_20240708/43');
                else if (serverFormId === 'ss_20240909-1' || formId === 'ss_20240909-1') router.push('/diagnostic/ss_20240909-1/43');
                else if (serverFormId === 'ss_20240909-2' || formId === 'ss_20240909-2') router.push('/diagnostic/ss_20240909-2/42');
                else if (serverFormId === 'ss_20240909-3' || formId === 'ss_20240909-3') router.push('/diagnostic/ss_20240909-3/37');
                else if (serverFormId === 'ss_20240909-4' || formId === 'ss_20240909-4') router.push('/diagnostic/ss_20240909-4/37');
                else if (serverFormId === 'ss_20241022' || formId === 'ss_20241022') router.push('/diagnostic/ss_20241022/37');
                else if (serverFormId === 'ss_DryishJanuary2025' || formId === 'ss_DryishJanuary2025') router.push('/diagnostic/ss_DryishJanuary2025/42');
                else if (serverFormId === 'ss_DryishJanuary2026' || formId === 'ss_DryishJanuary2026') router.push('/diagnostic/ss_DryishJanuary2026/6');
                else if (serverFormId === 'ss_SoberishMomDryishJanuary2025' || formId === 'ss_SoberishMomDryishJanuary2025') router.push('/diagnostic/ss_SoberishMomDryishJanuary2025/42');
                else if (serverFormId === 'ss_SoberishMomDryishJanuary2026' || formId === 'ss_SoberishMomDryishJanuary2026') router.push('/diagnostic/ss_SoberishMomDryishJanuary2026/6');
                else if (serverFormId === 'ss_UCBDryishJanuary2025' || formId === 'ss_UCBDryishJanuary2025') router.push('/diagnostic/ss_UCBDryishJanuary2025/42');
                else if (serverFormId === 'ss_ABCFitnessDryishJanuary2025' || formId === 'ss_ABCFitnessDryishJanuary2025') router.push('/diagnostic/ss_ABCFitnessDryishJanuary2025/42');
                else if (serverFormId === 'ss_SoberishMom' || formId === 'ss_SoberishMom') router.push('/diagnostic/ss_SoberishMom/42');
                else if (serverFormId === 'ss_20250122' || formId === 'ss_20250122') router.push('/diagnostic/ss_20250122/35');
                else if (serverFormId === 'ss_20250220' || formId === 'ss_20250220') router.push('/diagnostic/ss_20250220/35');
                else if (serverFormId === 'ss_20250220_fb' || formId === 'ss_20250220_fb') router.push('/diagnostic/ss_20250220_fb/35');
                else if (serverFormId === 'ss_Zumba' || formId === 'ss_Zumba') router.push('/diagnostic/ss_Zumba/40');
                else if (serverFormId === 'ss_BetterDrinkers' || formId === 'ss_BetterDrinkers') router.push('/diagnostic/ss_BetterDrinkers/24');
                else if (serverFormId === 'ss_20250618' || formId === 'ss_20250618') router.push('/diagnostic/ss_20250618/18');
                else if (serverFormId === 'ss_20250618_fb' || formId === 'ss_20250618_fb') router.push('/diagnostic/ss_20250618_fb/18');
                else if (serverFormId === 'ss_20250718_fb' || formId === 'ss_20250718_fb') router.push('/diagnostic/ss_20250718_fb/7');
                else if (serverFormId === 'ss_20250815' || formId === 'ss_20250815') router.push('/diagnostic/ss_20250815/6');
                else if (serverFormId === 'ss_SoberishOctober2025' || formId === 'ss_SoberishOctober2025') router.push('/diagnostic/ss_SoberishOctober2025/6');
                return;
            } else if (user.email && formId.startsWith('ss_')) {
                if (serverFormId === 'ss_20240708' || formId === 'ss_20240708') router.push('/diagnostic/ss_20240708/30');
                else if (serverFormId === 'ss_20240909-1' || formId === 'ss_20240909-1') router.push('/diagnostic/ss_20240909-1/14');
                else if (serverFormId === 'ss_20240909-2' || formId === 'ss_20240909-2') router.push('/diagnostic/ss_20240909-2/14');
                else if (serverFormId === 'ss_20240909-3' || formId === 'ss_20240909-3') router.push('/diagnostic/ss_20240909-3/14');
                else if (serverFormId === 'ss_20240909-4' || formId === 'ss_20240909-4') router.push('/diagnostic/ss_20240909-4/14');
                else if (serverFormId === 'ss_20241022' || formId === 'ss_20241022') router.push('/diagnostic/ss_20241022/14');
                else if (serverFormId === 'ss_DryishJanuary2025' || formId === 'ss_DryishJanuary2025') router.push('/diagnostic/ss_DryishJanuary2025/19');
                else if (serverFormId === 'ss_DryishJanuary2026' || formId === 'ss_DryishJanuary2026') router.push('/diagnostic/ss_DryishJanuary2026/6');
                else if (serverFormId === 'ss_SoberishMomDryishJanuary2025' || formId === 'ss_SoberishMomDryishJanuary2025') router.push('/diagnostic/ss_SoberishMomDryishJanuary2025/19');
                else if (serverFormId === 'ss_SoberishMomDryishJanuary2026' || formId === 'ss_SoberishMomDryishJanuary2026') router.push('/diagnostic/ss_SoberishMomDryishJanuary2026/6');
                else if (serverFormId === 'ss_UCBDryishJanuary2025' || formId === 'ss_UCBDryishJanuary2025') router.push('/diagnostic/ss_UCBDryishJanuary2025/19');
                else if (serverFormId === 'ss_ABCFitnessDryishJanuary2025' || formId === 'ss_ABCFitnessDryishJanuary2025') router.push('/diagnostic/ss_ABCFitnessDryishJanuary2025/19');
                else if (serverFormId === 'ss_SoberishMom' || formId === 'ss_SoberishMom') router.push('/diagnostic/ss_SoberishMom/19');
                else if (serverFormId === 'ss_20250122' || formId === 'ss_20250122') router.push('/diagnostic/ss_20250122/35');
                else if (serverFormId === 'ss_20250220' || formId === 'ss_20250220') router.push('/diagnostic/ss_20250220/35');
                else if (serverFormId === 'ss_20250220_fb' || formId === 'ss_20250220_fb') router.push('/diagnostic/ss_20250220_fb/35');
                else if (serverFormId === 'ss_Zumba' || formId === 'ss_Zumba') router.push('/diagnostic/ss_Zumba/40');
                else if (serverFormId === 'ss_BetterDrinkers' || formId === 'ss_BetterDrinkers') router.push('/diagnostic/ss_BetterDrinkers/24');
                else if (serverFormId === 'ss_20250618' || formId === 'ss_20250618') router.push('/diagnostic/ss_20250618/18');
                else if (serverFormId === 'ss_20250618_fb' || formId === 'ss_20250618_fb') router.push('/diagnostic/ss_20250618_fb/18');
                else if (serverFormId === 'ss_20250718_fb' || formId === 'ss_20250718_fb') router.push('/diagnostic/ss_20250718_fb/7');
                else if (serverFormId === 'ss_20250815' || formId === 'ss_20250815') router.push('/diagnostic/ss_20250815/6');
                else if (serverFormId === 'ss_SoberishOctober2025' || formId === 'ss_SoberishOctober2025') router.push('/diagnostic/ss_SoberishOctober2025/6');
                return;
            } else if (formId.startsWith('ss_')) {
                if (serverFormId === 'ss_20240708' || formId === 'ss_20240708') router.push('/diagnostic/ss_20240708/0');
                else if (serverFormId === 'ss_20240909-1' || formId === 'ss_20240909-1') router.push('/diagnostic/ss_20240909-1/0');
                else if (serverFormId === 'ss_20240909-2' || formId === 'ss_20240909-2') router.push('/diagnostic/ss_20240909-2/0');
                else if (serverFormId === 'ss_20240909-3' || formId === 'ss_20240909-3') router.push('/diagnostic/ss_20240909-3/0');
                else if (serverFormId === 'ss_20240909-4' || formId === 'ss_20240909-4') router.push('/diagnostic/ss_20240909-4/0');
                else if (serverFormId === 'ss_20241022' || formId === 'ss_20241022') router.push('/diagnostic/ss_20241022/0');
                else if (serverFormId === 'ss_DryishJanuary2025' || formId === 'ss_DryishJanuary2025') router.push('/diagnostic/ss_DryishJanuary2025/0');
                else if (serverFormId === 'ss_DryishJanuary2026' || formId === 'ss_DryishJanuary2026') router.push('/diagnostic/ss_DryishJanuary2026/0');
                else if (serverFormId === 'ss_SoberishMomDryishJanuary2025' || formId === 'ss_SoberishMomDryishJanuary2025') router.push('/diagnostic/ss_SoberishMomDryishJanuary2025/0');
                else if (serverFormId === 'ss_SoberishMomDryishJanuary2026' || formId === 'ss_SoberishMomDryishJanuary2026') router.push('/diagnostic/ss_SoberishMomDryishJanuary2026/0');
                else if (serverFormId === 'ss_UCBDryishJanuary2025' || formId === 'ss_UCBDryishJanuary2025') router.push('/diagnostic/ss_UCBDryishJanuary2025/0');
                else if (serverFormId === 'ss_ABCFitnessDryishJanuary2025' || formId === 'ss_ABCFitnessDryishJanuary2025') router.push('/diagnostic/ss_ABCFitnessDryishJanuary2025/0');
                else if (serverFormId === 'ss_SoberishMom' || formId === 'ss_SoberishMom') router.push('/diagnostic/ss_SoberishMom/0');
                else if (serverFormId === 'ss_20250122' || formId === 'ss_20250122') router.push('/diagnostic/ss_20250122/0');
                else if (serverFormId === 'ss_20250220' || formId === 'ss_20250220') router.push('/diagnostic/ss_20250220/0');
                else if (serverFormId === 'ss_20250220_fb' || formId === 'ss_20250220_fb') router.push('/diagnostic/ss_20250220_fb/0');
                else if (serverFormId === 'ss_Zumba' || formId === 'ss_Zumba') router.push('/diagnostic/ss_Zumba/0');
                else if (serverFormId === 'ss_BetterDrinkers' || formId === 'ss_BetterDrinkers') router.push('/diagnostic/ss_BetterDrinkers/0');
                else if (serverFormId === 'ss_20250618' || formId === 'ss_20250618') router.push('/diagnostic/ss_20250618/0');
                else if (serverFormId === 'ss_20250618_fb' || formId === 'ss_20250618_fb') router.push('/diagnostic/ss_20250618_fb/0');
                else if (serverFormId === 'ss_20250718_fb' || formId === 'ss_20250718_fb') router.push('/diagnostic/ss_20250718_fb/0');
                else if (serverFormId === 'ss_20250815' || formId === 'ss_20250815') router.push('/diagnostic/ss_20250815/0');
                else if (serverFormId === 'ss_SoberishOctober2025' || formId === 'ss_SoberishOctober2025') router.push('/diagnostic/ss_SoberishOctober2025/0');
                return;
            }
            if (user.phone && serverFormId) {
                router.push('/register/phone');
                return;
            }
            serverFormId
                ? router.push({
                    pathname: `/typeform-diagnostic/${serverFormId}`,
                    query: {
                        answerRef: 'intro',
                        formId
                    }
                })
                : router.push({
                    pathname: `/typeform-diagnostic/${formId}`,
                    query: {
                        answerRef: 'intro',
                        formId
                    }
                });
            return;
        } else if (!user.diagnosticInFlight) {
            if (!user.phoneAuthorized) {
                if (isMedFirstOrWinback) {
                    router.push(`/mf/intake/${user.formId}`); // TODO YEHIA: Figure something out
                    return;
                } else if (isMedUpsellWinback) {
                    router.push('/medication/intake'); // TODO YEHIA: Figure something out
                    return;
                }
                if (formId.startsWith('ss_')) {
                    if (formId === 'ss_20240708') router.push('/diagnostic/ss_20240708/43');
                    else if (formId === 'ss_20240909-1') router.push('/diagnostic/ss_20240909-1/43');
                    else if (formId === 'ss_20240909-2') router.push('/diagnostic/ss_20240909-2/42');
                    else if (formId === 'ss_20240909-3') router.push('/diagnostic/ss_20240909-3/37');
                    else if (formId === 'ss_20240909-4') router.push('/diagnostic/ss_20240909-4/37');
                    else if (formId === 'ss_20241022') router.push('/diagnostic/ss_20241022/37');
                    else if (formId === 'ss_DryishJanuary2025') router.push('/diagnostic/ss_DryishJanuary2025/42');
                    else if (formId === 'ss_DryishJanuary2026') router.push('/diagnostic/ss_DryishJanuary2026/6');
                    else if (formId === 'ss_SoberishMomDryishJanuary2025') router.push('/diagnostic/ss_SoberishMomDryishJanuary2025/42');
                    else if (formId === 'ss_SoberishMomDryishJanuary2026') router.push('/diagnostic/ss_SoberishMomDryishJanuary2026/6');
                    else if (formId === 'ss_UCBDryishJanuary2025') router.push('/diagnostic/ss_UCBDryishJanuary2025/42');
                    else if (formId === 'ss_ABCFitnessDryishJanuary2025') router.push('/diagnostic/ss_ABCFitnessDryishJanuary2025/42');
                    else if (formId === 'ss_SoberishMom') router.push('/diagnostic/ss_SoberishMom/42');
                    else if (formId === 'ss_20250122') router.push('/diagnostic/ss_20250122/35');
                    else if (formId === 'ss_20250220') router.push('/diagnostic/ss_20250220/35');
                    else if (formId === 'ss_20250220_fb') router.push('/diagnostic/ss_20250220_fb/35');
                    else if (formId === 'ss_Zumba') router.push('/diagnostic/ss_Zumba/40');
                    else if (formId === 'ss_BetterDrinkers') router.push('/diagnostic/ss_BetterDrinkers/24');
                    else if (formId === 'ss_20250618') router.push('/diagnostic/ss_20250618/18');
                    else if (formId === 'ss_20250618_fb') router.push('/diagnostic/ss_20250618_fb/18');
                    else if (formId === 'ss_20250718_fb') router.push('/diagnostic/ss_20250718_fb/7');
                    else if (formId === 'ss_20250815') router.push('/diagnostic/ss_20250815/6');
                    else if (formId === 'ss_SoberishOctober2025') router.push('/diagnostic/ss_SoberishOctober2025/6');
                    return;
                }
                router.push('/register/phone');
                return;
            }
            /*
             * TODO: Payments V2 — deferred. Routing still reads userPaymentEntity while the
             * dashboard reads user_subscription: grace_period has no legacy analogue, falls
             * through to UNKNOWN, and bounces the user to /diagnostic/target at login.
             * When migrating, handle NO_SUBSCRIPTION alongside UNKNOWN below.
             */
            const userPaymentEntity = await get('payments-gateway/my-payment-entity');
            if(!userPaymentEntity || (!userPaymentEntity.paymentProcessor && !userPaymentEntity.paymentMethodStatus)) {
                if (cookies?.ss_app === 'true' || appUserAgent?.isApp) {
                    if (formId.startsWith('ss_') || (isMedFirstOrWinback || isMedUpsellWinback)) {
                        router.push('/app-bridge');
                        return;
                    }
                    router.push({
                        pathname: '/diagnostic/target',
                        query: {
                            redirect: '/app-bridge'
                        }
                    });
                    return;
                }
                if (isMedFirstOrWinback) {
                    router.push(`/mf/intake/${user.formId}`); // TODO YEHIA: Figure something out
                    return;
                } else if (isMedUpsellWinback) {
                    router.push('/medication/intake'); // TODO YEHIA: Figure something out
                    return;
                }
                if (formId.startsWith('ss_')) {
                    if (formId === 'ss_20240708') router.push('/diagnostic/ss_20240708/52');
                    else if (formId === 'ss_20240909-1') router.push('/diagnostic/ss_20240909-1/52');
                    else if (formId === 'ss_20240909-2') router.push('/diagnostic/ss_20240909-2/51');
                    else if (formId === 'ss_20240909-3') router.push('/diagnostic/ss_20240909-3/49');
                    else if (formId === 'ss_20240909-4') router.push('/diagnostic/ss_20240909-4/49');
                    else if (formId === 'ss_20241022') router.push('/diagnostic/ss_20241022/49');
                    else if (formId === 'ss_DryishJanuary2025') router.push('/diagnostic/ss_DryishJanuary2025/54');
                    else if (formId === 'ss_DryishJanuary2026') router.push('/diagnostic/ss_DryishJanuary2026/7');
                    else if (formId === 'ss_SoberishMomDryishJanuary2025') router.push('/diagnostic/ss_SoberishMomDryishJanuary2025/54');
                    else if (formId === 'ss_SoberishMomDryishJanuary2026') router.push('/diagnostic/ss_SoberishMomDryishJanuary2026/7');
                    else if (formId === 'ss_UCBDryishJanuary2025') router.push('/diagnostic/ss_UCBDryishJanuary2025/54');
                    else if (formId === 'ss_ABCFitnessDryishJanuary2025') router.push('/diagnostic/ss_ABCFitnessDryishJanuary2025/54');
                    else if (formId === 'ss_SoberishMom') router.push('/diagnostic/ss_SoberishMom/54');
                    else if (formId === 'ss_20250122') router.push('/diagnostic/ss_20250122/47');
                    else if (formId === 'ss_20250220') router.push('/diagnostic/ss_20250220/47');
                    else if (formId === 'ss_20250220_fb') router.push('/diagnostic/ss_20250220_fb/47');
                    else if (formId === 'ss_Zumba') router.push('/diagnostic/ss_Zumba/52');
                    else if (formId === 'ss_BetterDrinkers') router.push('/diagnostic/ss_BetterDrinkers/26');
                    else if (formId === 'ss_20250618') router.push('/diagnostic/ss_20250618/24');
                    else if (formId === 'ss_20250618_fb') router.push('/diagnostic/ss_20250618_fb/24');
                    else if (formId === 'ss_20250718_fb') router.push('/diagnostic/ss_20250718_fb/11');
                    else if (formId === 'ss_20250815') router.push('/diagnostic/ss_20250815/7');
                    else if (formId === 'ss_SoberishOctober2025') router.push('/diagnostic/ss_SoberishOctober2025/7');
                    return;
                }
                router.push('/diagnostic/target');
                return;
            }

            const subscriptionState: SubscriptionState = await interpretUserPaymentEntityState(user, userPaymentEntity);
            switch(subscriptionState) {
            case SubscriptionState.UNKNOWN:
                if (cookies?.ss_app === 'true' || appUserAgent?.isApp) {
                    if (formId.startsWith('ss_') || (isMedFirstOrWinback || isMedUpsellWinback)) {
                        router.push('/app-bridge');
                        return;
                    }
                    router.push({
                        pathname: '/diagnostic/target',
                        query: {
                            redirect: '/app-bridge'
                        }
                    });
                    break;
                }
                if (isMedFirstOrWinback) {
                    router.push(`/mf/intake/${user.formId}`); // TODO YEHIA: Figure something out
                    return;
                } else if (isMedUpsellWinback) {
                    router.push('/medication/intake'); // TODO YEHIA: Figure something out
                    return;
                }
                if (formId.startsWith('ss_')) {
                    if (formId === 'ss_20240708') router.push('/diagnostic/ss_20240708/52');
                    else if (formId === 'ss_20240909-1') router.push('/diagnostic/ss_20240909-1/52');
                    else if (formId === 'ss_20240909-2') router.push('/diagnostic/ss_20240909-2/51');
                    else if (formId === 'ss_20240909-3') router.push('/diagnostic/ss_20240909-3/49');
                    else if (formId === 'ss_20240909-4') router.push('/diagnostic/ss_20240909-4/49');
                    else if (formId === 'ss_20241022') router.push('/diagnostic/ss_20241022/49');
                    else if (formId === 'ss_DryishJanuary2025') router.push('/diagnostic/ss_DryishJanuary2025/54');
                    else if (formId === 'ss_DryishJanuary2026') router.push('/diagnostic/ss_DryishJanuary2026/7');
                    else if (formId === 'ss_SoberishMomDryishJanuary2025') router.push('/diagnostic/ss_SoberishMomDryishJanuary2025/54');
                    else if (formId === 'ss_SoberishMomDryishJanuary2026') router.push('/diagnostic/ss_SoberishMomDryishJanuary2026/7');
                    else if (formId === 'ss_UCBDryishJanuary2025') router.push('/diagnostic/ss_UCBDryishJanuary2025/54');
                    else if (formId === 'ss_ABCFitnessDryishJanuary2025') router.push('/diagnostic/ss_ABCFitnessDryishJanuary2025/54');
                    else if (formId === 'ss_SoberishMom') router.push('/diagnostic/ss_SoberishMom/54');
                    else if (formId === 'ss_20250122') router.push('/diagnostic/ss_20250122/47');
                    else if (formId === 'ss_20250220') router.push('/diagnostic/ss_20250220/47');
                    else if (formId === 'ss_20250220_fb') router.push('/diagnostic/ss_20250220_fb/47');
                    else if (formId === 'ss_Zumba') router.push('/diagnostic/ss_Zumba/52');
                    else if (formId === 'ss_BetterDrinkers') router.push('/diagnostic/ss_BetterDrinkers/26');
                    else if (formId === 'ss_20250618') router.push('/diagnostic/ss_20250618/24');
                    else if (formId === 'ss_20250618_fb') router.push('/diagnostic/ss_20250618_fb/24');
                    else if (formId === 'ss_20250718_fb') router.push('/diagnostic/ss_20250718_fb/11');
                    else if (formId === 'ss_20250815') router.push('/diagnostic/ss_20250815/7');
                    else if (formId === 'ss_SoberishOctober2025') router.push('/diagnostic/ss_SoberishOctober2025/7');
                    return;
                }
                router.push('/diagnostic/target');
                break;
            case SubscriptionState.NO_CREDIT_CARD_TRIAL:
            case SubscriptionState.CREDIT_CARD_TRIAL:
            case SubscriptionState.ACTIVE:
            case SubscriptionState.ACTIVE_WITH_PAYMENT_ERROR:
            case SubscriptionState.CANCELED:
            default: {
                if (cookies?.ss_app === 'true' || appUserAgent?.isApp) {
                    router.push('/app-bridge');
                    break;
                }
                if (redirectUrl) {
                    router.push(redirectUrl as string);
                    return;
                }
                router.push(isUserFirstWeek ? '/dashboard/tracking' : '/dashboard/progress');
                break;
            }
            }
        }
        if (cookies?.ss_app === 'true' || appUserAgent?.isApp) {
            router.push('/app-bridge?redirect=LoginStackNav');
            return;
        }
        if (redirectUrl) {
            router.push(redirectUrl as string);
            return;
        }
        router.push(isUserFirstWeek ? '/dashboard/tracking' : '/dashboard/progress');
        return;
    };

    switch (user.flowType) {
    // ptcc -> Phone to credit card trial users
    case FlowType.PTCC: {
        // Give user access to checkout paths with type 'ptcc'
        if (user.diagnosticInFlight && !user.diagnosticInFlightWithSub && !formId.startsWith('ss_')) {
            const planLinks = ['/subscription/recommended', '/subscription/plans', '/subscription_old/checkout'];
            if (planLinks.includes(router.pathname)) {
                setHasAccess(true);
            } else {
                router.push('/subscription/recommended');
            }
        } else if (user.diagnosticInFlightWithSub) {
            if (router.pathname === '/start/profile') {
                setHasAccess(true);
            } else {
                // User has sub but has not finished diagnostic redirect them to the 'bare-bones' profile page
                router.push('/start/profile');
            }
        } else {
            defaultRedirectFlow();
        }
        break;
    }
    default: defaultRedirectFlow();
    }

}

/**
 *
 * @param context serverSidePropsContext
 * @param response api response (should be with a jwt protected endpoint)
 * @param serverSideResponse normal serverSideResponse
 * @returns server side redirect if user is unauthorized (redirects to login url with redirect param)
 *     or returns normal serverSideResponse
 */
export async function serverSidePropsAuthResponse(
    context: GetServerSidePropsContext,
    response: any,
    serverSideResponse: GetServerSidePropsResult<any>
): Promise<GetServerSidePropsResult<any>> {
    if (response?.statusCode === 401) {
        return {
            redirect: {
                permanent: false,
                destination: '/login/phone?redirectUrl=' + encodeURIComponent(context.resolvedUrl)
            },
            props: {}
        };
    }
    return serverSideResponse;
}
